import { isCapacitor } from '@tbitop/platform';
import type Keycloak from 'keycloak-js';
import type { KeycloakConfig } from 'keycloak-js';
import { isUndefined } from 'lodash-es';
import { log } from '../log';
import type { Tokens } from '../tokens';
import { KeycloakInitializationFailedError } from './errors';
import type { PlatformAuthenticationConfig } from './platform-authentication-config';

export async function createKeycloakInstance(
  { clientId, getInstanceUrl, redirectUri, createAdapter }: PlatformAuthenticationConfig,
  realm: string,
  tokens?: Tokens,
) {
  log.debug(`Instantiating Keycloak instance for client ID ${clientId} and realm ${realm}.`);

  const url = getInstanceUrl();
  const instance = await instantiateKeycloak({ url, realm, clientId });

  const options = await getKeycloakInitOptions(
    {
      redirectUri,
      adapter: createAdapter(instance),
    },
    tokens,
  );

  log.debug('Initialising Keycloak instance with options.', options);

  try {
    await instance.init(options);
  } catch (error) {
    if (!isUndefined(tokens)) {
      // When .init() is called with invalid tokens, keycloak-js throws
      // an undefined error. We assume that this is the case for now.
      // If there was another issue, calling login() later on will probably
      // throw the same issue again, so we can ignore it here for now.
      log.warn('Keycloak initialisation failed, probably because of outdated tokens. Nothing to see here, move along.');
    } else {
      throw new KeycloakInitializationFailedError(error);
    }
  }

  return instance;
}

async function instantiateKeycloak(config: KeycloakConfig): Promise<Keycloak> {
  // keycloak-js references window on import which breaks the Next.js build,
  // so we need to load it dynamically
  const { default: Keycloak } = await import('@tbitop/keycloak');
  return new Keycloak(config);
}

async function getKeycloakInitOptions(
  initOptions: Keycloak.KeycloakInitOptions,
  tokens?: Tokens,
) {
  let options: Keycloak.KeycloakInitOptions = {
    ...initOptions,
    enableLogging: true,
    pkceMethod: 'S256',
  };

  if (isCapacitor()) {
    options = {
      ...options,
      checkLoginIframe: false,
    };

    if (!isUndefined(tokens)) {
      const { accessToken: token, idToken, refreshToken } = tokens;
      options = {
        ...options,
        token,
        idToken,
        refreshToken,
      };
    }
  } else {
    // Silently checking SSO only works when client and Keycloak are served by the same origin (e.g. portal.team-beverage.dev).
    // When working locally, client and Keycloak are served from different origins (localhost:4xxx vs. portal.team-beverage.dev)
    // and silently checking SSO fails with an endless authentication redirect loop
    if (process.env.NX_SKIP_SILENT_LOGIN !== 'true') {
      options = {
        ...options,
        checkLoginIframe: true,
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: getSilentCheckSsoUrl(),
      };
    }
  }

  return options;
}

function getSilentCheckSsoUrl() {
  const url = new URL(window.location.origin);
  url.pathname = '/assets/silent-check-sso.html';
  return url.toString();
}
